import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useMutation } from '@tanstack/react-query';
import { API_CONFIG } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useUserStore } from '../stores/user';
import { useGameStore } from '../stores/game';
import {
  BATCH_MIN_PHOTOS,
  BATCH_MAX_PHOTOS,
  PROFILE_VERTICALS,
  PROFILE_VERTICAL_CONFIGS,
  getRankingSequenceRecommendation,
  getProjectIdForVertical,
  getRatingContextForVertical,
  getWinnerConfidence,
  isProfileVertical,
  type ProfileVertical,
} from '@photofeeler/shared';
import { showToast } from '../components/Toast';

interface UploadResponse {
  objectKey: string;
  contentType: string;
  sizeBytes: number;
}

interface PhotoEntry {
  file: File;
  preview: string;
  status: 'pending' | 'uploading' | 'analyzing' | 'done' | 'error';
  objectKey?: string;
  photoId?: string;
  score?: number;
}

function getBatchCredits(count: number): number {
  if (count <= 5) return 3;
  if (count <= 10) return 5;
  if (count <= 20) return 8;
  return 12;
}

export function Shootout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, session } = useAuth();
  const { credits, setCredits } = useUserStore();
  const { projects, activeProjectId, setActiveProject, recordPhotoAnalysis, recordCompareRun } = useGameStore();
  const activeProject =
    projects.find(project => project.id === activeProjectId) ||
    projects.find(project => project.vertical === 'dating') ||
    projects[0];
  const requestedVertical = searchParams.get('vertical');
  const initialVertical = isProfileVertical(requestedVertical)
    ? requestedVertical
    : activeProject?.vertical || 'dating';
  const [photos, setPhotos] = useState<PhotoEntry[]>([]);
  const [vertical, setVertical] = useState<ProfileVertical>(initialVertical);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState<'upload' | 'processing' | 'reveal'>('upload');
  const [revealStep, setRevealStep] = useState(0);
  const revealTimeout = useRef<ReturnType<typeof setTimeout>>();
  const activeConfig = PROFILE_VERTICAL_CONFIGS[vertical];
  const context = getRatingContextForVertical(vertical);

  const batchCredits = getBatchCredits(photos.length);
  const canStart = photos.length >= BATCH_MIN_PHOTOS && credits >= batchCredits && !running;

  useEffect(() => {
    if (isProfileVertical(requestedVertical)) {
      setVertical(requestedVertical);
    }
  }, [requestedVertical]);

  const onDrop = useCallback((files: File[]) => {
    const remaining = BATCH_MAX_PHOTOS - photos.length;
    const toAdd = files.slice(0, remaining).map(file => ({
      file,
      preview: URL.createObjectURL(file),
      status: 'pending' as const,
    }));
    setPhotos(prev => [...prev, ...toAdd]);
  }, [photos.length]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    noClick: true,
    noKeyboard: true,
    maxFiles: BATCH_MAX_PHOTOS,
  });

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      photos.forEach(p => URL.revokeObjectURL(p.preview));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removePhoto = (index: number) => {
    setPhotos(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadMutation = useMutation({
    mutationFn: async (file: File): Promise<UploadResponse> => {
      const h: Record<string, string> = {};
      if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_CONFIG.UPLOAD_URL}/upload`, { method: 'POST', headers: h, body: fd });
      if (!res.ok) throw new Error('We couldn\u0027t upload this photo. Check your connection and try again.');
      return res.json() as Promise<UploadResponse>;
    },
  });

  const rateMutation = useMutation({
    mutationFn: async ({ objectKey, ctx }: { objectKey: string; ctx: string }): Promise<{ photoId: string; credits?: number }> => {
      const h: Record<string, string> = { 'Content-Type': 'application/json' };
      if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
      const res = await fetch(`${API_CONFIG.RATE_URL}/rate`, {
        method: 'POST', headers: h,
        body: JSON.stringify({ objectKey, context: ctx }),
      });
      if (!res.ok) {
        if (res.status === 402) throw new Error('You\u0027re out of credits. Grab more to keep testing photos.');
        throw new Error('Something went wrong starting the analysis. Please try again.');
      }
      return res.json() as Promise<{ photoId: string; credits?: number }>;
    },
  });

  const startShootout = async () => {
    if (!canStart) return;
    setRunning(true);
    setPhase('processing');

    const updatedPhotos = [...photos];

    // Step 1: Upload all photos
    for (let i = 0; i < updatedPhotos.length; i++) {
      updatedPhotos[i] = { ...updatedPhotos[i], status: 'uploading' };
      setPhotos([...updatedPhotos]);
      try {
        const result = await uploadMutation.mutateAsync(updatedPhotos[i].file);
        updatedPhotos[i] = { ...updatedPhotos[i], objectKey: result.objectKey, status: 'analyzing' };
      } catch {
        updatedPhotos[i] = { ...updatedPhotos[i], status: 'error' };
        setPhotos([...updatedPhotos]);
      }
    }
    setPhotos([...updatedPhotos]);

    // Step 2: Submit all for analysis
    const photoIds: string[] = [];
    for (let i = 0; i < updatedPhotos.length; i++) {
      if (!updatedPhotos[i].objectKey || updatedPhotos[i].status === 'error') continue;
      try {
        const result = await rateMutation.mutateAsync({ objectKey: updatedPhotos[i].objectKey!, ctx: context });
        updatedPhotos[i] = { ...updatedPhotos[i], photoId: result.photoId };
        photoIds.push(result.photoId);
        if (result.credits !== undefined) setCredits(result.credits);
      } catch {
        updatedPhotos[i] = { ...updatedPhotos[i], status: 'error' };
      }
    }
    setPhotos([...updatedPhotos]);

    // Step 3: Poll for all results
    const pollResults = async () => {
      let allDone = false;
      while (!allDone) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        allDone = true;
        for (let i = 0; i < updatedPhotos.length; i++) {
          if (!updatedPhotos[i].photoId || updatedPhotos[i].status === 'done' || updatedPhotos[i].status === 'error') continue;
          try {
            const res = await fetch(`${API_CONFIG.RATE_URL}/ratings/${updatedPhotos[i].photoId}`, {
              headers: { 'Authorization': `Bearer ${session?.access_token}` },
            });
            if (res.ok) {
              const data = await res.json() as { aggregate?: { composite_score: number } | null; status?: string };
              if (data.aggregate) {
                updatedPhotos[i] = { ...updatedPhotos[i], score: data.aggregate.composite_score, status: 'done' };
                setPhotos([...updatedPhotos]);
              } else if (data.status === 'failed') {
                updatedPhotos[i] = { ...updatedPhotos[i], status: 'error' };
                setPhotos([...updatedPhotos]);
              } else {
                allDone = false;
              }
            } else {
              allDone = false;
            }
          } catch {
            allDone = false;
          }
        }
      }
    };

    await pollResults();

    const completedRankings = updatedPhotos
      .filter((photo): photo is PhotoEntry & { photoId: string; score: number } =>
        photo.status === 'done' && !!photo.photoId && typeof photo.score === 'number'
      )
      .map(photo => ({
        photoId: photo.photoId,
        score: photo.score,
      }))
      .sort((a, b) => a.score - b.score);

    if (completedRankings.length > 0) {
      const recordedAt = new Date().toISOString();
      completedRankings.forEach(ranking => {
        recordPhotoAnalysis({
          vertical,
          photoId: ranking.photoId,
          score: ranking.score,
          recordedAt,
        });
      });

      if (completedRankings.length >= 2) {
        const winner = completedRankings.at(-1)!;
        const projectId = getProjectIdForVertical(vertical);
        recordCompareRun(projectId, {
          id: `${projectId}-${recordedAt}`,
          winnerPhotoId: winner.photoId,
          winnerScore: winner.score,
          rankings: completedRankings,
          createdAt: recordedAt,
        });
        setActiveProject(projectId);
      }
    }

    // Step 4: Trigger reveal
    setPhase('reveal');
    setRevealStep(0);

    // Animate reveal: 0=blur, 1=scores count, 2=sort, 3=winner
    revealTimeout.current = setTimeout(() => setRevealStep(1), 500);
    setTimeout(() => setRevealStep(2), 2000);
    setTimeout(() => setRevealStep(3), 3000);

    setRunning(false);
  };

  // Cleanup timeout
  useEffect(() => {
    return () => { if (revealTimeout.current) clearTimeout(revealTimeout.current); };
  }, []);

  if (!isAuthenticated) { navigate('/'); return null; }

  const completedPhotos = photos.filter(p => p.status === 'done' && p.score != null);
  const ranked = [...completedPhotos].sort((a, b) => (b.score || 0) - (a.score || 0));
  const winner = ranked[0];
  const rankingConfidence =
    ranked.length >= 2
      ? getWinnerConfidence(
          ranked.map(photo => ({
            photoId: photo.photoId || '',
            score: photo.score || 0,
          }))
        )
      : null;
  const sequenceRecommendation =
    ranked.length >= 2
      ? getRankingSequenceRecommendation(
          vertical,
          ranked.map(photo => ({
            photoId: photo.photoId || '',
            score: photo.score || 0,
          }))
        )
      : null;
  const revealTitle = vertical === 'linkedin' ? 'Choose your primary headshot' : 'Build your strongest dating lineup';
  const revealSubtitle =
    vertical === 'linkedin'
      ? 'Pick the clearest recruiter-facing winner, then keep one or two strong backups.'
      : 'Pick the best lead photo first, then order the rest of the set so the lineup stays strong.';

  const scoreColor = (s: number) => s >= 80 ? 'text-emerald-600' : s >= 60 ? 'text-blue-600' : s >= 40 ? 'text-amber-600' : 'text-rose';
  const scoreLabel = (s: number) => s >= 80 ? 'Excellent' : s >= 60 ? 'Good' : s >= 40 ? 'Room to improve' : 'Try different';

  return (
    <div className="relative overflow-hidden pt-24 pb-16 px-5 sm:px-8 max-w-5xl mx-auto animate-fade-in" {...getRootProps()}>
      <input {...getInputProps()} />
      <div className="page-orb left-[-5rem] top-28 h-72 w-72 bg-rose/15" />
      <div className="page-orb right-[-4rem] top-24 h-80 w-80 bg-info/10" />

      <div className="page-hero-panel mb-10">
        <div className="flex items-start justify-between gap-5">
          <div>
            <div className="premium-kicker mb-3">Compare</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-ink" style={{ fontWeight: 600 }}>Compare</h1>
            <p className="hero-copy mt-3 max-w-2xl">
              Upload 2-50 {activeConfig.shortLabel.toLowerCase()} photos and reveal the strongest lead image for this workspace.
            </p>
          </div>
          <button onClick={() => navigate('/app')} className="btn-ghost text-xs shrink-0">← Back</button>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mt-6">
          <div className="premium-stat">
            <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>{activeConfig.label}</div>
            <div className="text-xs text-ink-faint mt-1">Active workspace</div>
          </div>
          <div className="premium-stat">
            <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>{photos.length || 0}</div>
            <div className="text-xs text-ink-faint mt-1">Photos loaded</div>
          </div>
          <div className="premium-stat">
            <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>{batchCredits}</div>
            <div className="text-xs text-ink-faint mt-1">Credits required</div>
          </div>
        </div>
      </div>

      {/* === UPLOAD PHASE === */}
      {phase === 'upload' && (
        <>
          {/* Explainer — only when no photos added yet */}
          {photos.length === 0 && (
            <div className="card mb-8 flex items-start gap-5 lift-hover">
              <div className="text-3xl">🏆</div>
              <div>
                <h3 className="text-sm font-sans font-medium text-ink mb-1">How compare works</h3>
                <ol className="text-xs text-ink-faint space-y-1" style={{ fontWeight: 300 }}>
                  <li>1. Add 2-50 photos you&apos;re choosing between</li>
                  <li>2. Pick a workspace (Dating or LinkedIn)</li>
                  <li>3. All photos get rated by 3 AI models simultaneously</li>
                  <li>4. See the ranked results — your best photo wins</li>
                </ol>
                <p className="text-xs text-ink-faint mt-2" style={{ fontWeight: 300 }}>Batch pricing: 3 credits for 2-5, 5 credits for 6-10, 8 credits for 11-20, 12 credits for 21-50.</p>
              </div>
            </div>
          )}

          {/* Photo grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {photos.map((p, i) => (
              <div key={i} className="relative group rounded-xl overflow-hidden border border-surface-border aspect-square">
                <img src={p.preview} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={(e) => { e.stopPropagation(); removePhoto(i); }}
                  className="absolute top-2 right-2 w-6 h-6 bg-black/50 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ×
                </button>
                <div className="absolute bottom-2 left-2 text-[10px] text-white bg-black/40 px-2 py-0.5 rounded font-sans">
                  #{i + 1}
                </div>
              </div>
            ))}
            {photos.length < BATCH_MAX_PHOTOS && (
              <div
                onClick={(e) => { e.stopPropagation(); open(); }}
                className={`rounded-xl border-2 border-dashed aspect-square flex flex-col items-center justify-center cursor-pointer transition-colors ${isDragActive ? 'border-rose bg-rose-faint' : 'border-surface-border hover:border-rose/30'}`}
              >
                <span className="text-2xl text-ink-faint mb-1">+</span>
                <span className="text-[10px] text-ink-faint font-sans">Add photo</span>
                <span className="text-[10px] text-ink-faint font-sans">{photos.length}/{BATCH_MAX_PHOTOS}</span>
              </div>
            )}
          </div>

          {/* Workspace picker */}
            <div className="card mb-6 lift-hover">
            <h2 className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-3">Workspace</h2>
            <div className="flex gap-3">
              {PROFILE_VERTICALS.map(verticalOption => (
                <button
                  key={verticalOption}
                  onClick={() => setVertical(verticalOption)}
                  className={`px-4 py-2 rounded-lg text-xs font-sans transition-all ${
                    vertical === verticalOption
                      ? 'bg-rose text-white font-medium'
                      : 'bg-cream-warm text-ink-faint hover:text-ink'
                  }`}
                >
                  {PROFILE_VERTICAL_CONFIGS[verticalOption].label}
                </button>
              ))}
            </div>
          </div>

          {/* Cost & Start */}
          <div className="card-rose">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm font-sans text-ink" style={{ fontWeight: 500 }}>
                  {photos.length} photo{photos.length !== 1 ? 's' : ''} · {batchCredits} credit{batchCredits !== 1 ? 's' : ''}
                </div>
                <div className="text-[11px] text-ink-faint" style={{ fontWeight: 300 }}>
                  {photos.length <= 5 ? '3 credits for 2-5 photos' : photos.length <= 10 ? '5 credits for 6-10 photos' : photos.length <= 20 ? '8 credits for 11-20 photos' : '12 credits for 21-50 photos'} · You have {credits}
                </div>
              </div>
              {credits < batchCredits && photos.length >= BATCH_MIN_PHOTOS && (
                <button onClick={() => navigate('/purchase')} className="btn-secondary text-xs">Buy credits</button>
              )}
            </div>
            {photos.length < BATCH_MIN_PHOTOS && (
              <p className="text-xs text-ink-faint mb-4" style={{ fontWeight: 300 }}>Add at least {BATCH_MIN_PHOTOS} photos to start.</p>
            )}
            <button onClick={startShootout} disabled={!canStart} className="btn-rose w-full">
              Start compare
            </button>
          </div>
        </>
      )}

      {/* === PROCESSING PHASE === */}
      {phase === 'processing' && (
        <div className="max-w-md mx-auto">
          <div className="text-center mb-10">
            <div className="w-10 h-10 border-2 border-ink/10 border-t-rose rounded-full animate-spin mx-auto mb-6" />
            <h2 className="font-sans text-2xl text-ink mb-2">Analyzing {photos.length} photos...</h2>
            <p className="text-sm text-ink-faint" style={{ fontWeight: 300 }}>Each photo is rated by 3 AI models.</p>
          </div>
          <div className="space-y-3">
            {photos.map((p, i) => (
              <div key={i} className="flex items-center gap-4 card p-3">
                <img src={p.preview} alt="" className="w-12 h-12 rounded-lg object-cover" />
                <div className="flex-1">
                  <div className="text-sm font-sans text-ink">Photo #{i + 1}</div>
                  <div className="text-[11px] text-ink-faint capitalize">{p.status === 'done' ? `Score: ${p.score}` : p.status}</div>
                </div>
                {p.status === 'done' && <span className="text-rose text-xs">✓</span>}
                {p.status === 'error' && <span className="text-rose text-xs">✗</span>}
                {(p.status === 'uploading' || p.status === 'analyzing') && (
                  <div className="w-4 h-4 border border-ink/10 border-t-rose rounded-full animate-spin" />
                )}
              </div>
            ))}
          </div>
          <div className="mt-6 h-1 bg-surface-border rounded-full overflow-hidden">
            <div className="h-full bg-rose/60 rounded-full transition-all duration-500" style={{ width: `${(completedPhotos.length / photos.length) * 100}%` }} />
          </div>
        </div>
      )}

      {/* === REVEAL PHASE === */}
      {phase === 'reveal' && (
        <div>
          {/* Winner card */}
          {revealStep >= 3 && winner && (
            <div className="card-rose text-center py-10 mb-8 animate-slide-up">
              <div className="text-[11px] font-sans text-rose tracking-widest uppercase mb-3 font-medium">
                {vertical === 'linkedin' ? 'Primary headshot' : 'Best lead photo'}
              </div>
              <img src={winner.preview} alt="Winner" className="w-32 h-32 rounded-2xl object-cover mx-auto mb-4 border-2 border-rose/30 shadow-warm" />
              <div className={`text-6xl font-sans ${scoreColor(winner.score!)} mb-1`} style={{ fontWeight: 600, letterSpacing: '-0.04em' }}>
                {winner.score}
              </div>
              <div className="text-sm font-sans text-ink" style={{ fontWeight: 500 }}>{scoreLabel(winner.score!)}</div>
              <p className="text-xs text-ink-faint mt-3 max-w-md mx-auto" style={{ fontWeight: 300 }}>
                {vertical === 'linkedin'
                  ? 'Use this headshot first on LinkedIn. Keep the next strongest option as backup for future profile refreshes.'
                  : 'Use this first in your dating profile, then let the next photos add range without weakening the opener.'}
              </p>
              <div className="flex justify-center gap-3 mt-6">
                <button onClick={() => navigate(`/results/${winner.photoId}`)} className="btn-rose text-xs">View full analysis</button>
                <button onClick={() => {
                  const text = `My best photo scored ${winner.score}/100 in a ${photos.length}-photo compare! Rated by AI on PhotoFeeler`;
                  navigator.clipboard.writeText(text).then(() => showToast('Copied!', 'success'));
                }} className="btn-secondary text-xs">Share result</button>
              </div>
            </div>
          )}

          {revealStep >= 3 && rankingConfidence && sequenceRecommendation && (
            <div className="grid lg:grid-cols-[0.9fr_1.3fr] gap-5 mb-8 animate-slide-up">
              <div className="card">
                <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                  {vertical === 'linkedin' ? 'Decision quality' : 'Lead-photo confidence'}
                </div>
                <h2 className="font-sans text-xl text-ink mb-2" style={{ fontWeight: 600 }}>
                  {rankingConfidence.label}
                </h2>
                <p className="text-sm text-ink-muted leading-relaxed mb-4" style={{ fontWeight: 300 }}>
                  {rankingConfidence.detail}
                </p>
                <div className="rounded-2xl border border-surface-border bg-cream-warm px-4 py-3">
                  <div className="text-[10px] uppercase tracking-wider text-ink-faint mb-1">Top-score gap</div>
                  <div className="text-2xl font-sans text-ink" style={{ fontWeight: 600 }}>
                    {rankingConfidence.scoreGap}
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                  {vertical === 'linkedin' ? 'Headshot shortlist' : 'Photo order'}
                </div>
                <h2 className="font-sans text-xl text-ink mb-1" style={{ fontWeight: 600 }}>
                  {revealTitle}
                </h2>
                <p className="text-sm text-ink-muted mb-4" style={{ fontWeight: 300 }}>
                  {revealSubtitle}
                </p>
                <div className="space-y-3">
                  {sequenceRecommendation.items.map(item => {
                    const photo = ranked.find(entry => entry.photoId === item.photoId);
                    return (
                      <div key={item.photoId} className="flex items-start gap-3 rounded-xl border border-surface-border bg-cream-warm px-3 py-3">
                        <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-white text-xs font-sans text-ink">
                          {item.position}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>
                              {item.title}
                            </div>
                            <div className="text-[11px] font-sans text-ink-faint">
                              {photo?.score ?? item.score}
                            </div>
                          </div>
                          <p className="text-xs text-ink-faint mt-1 leading-relaxed" style={{ fontWeight: 300 }}>
                            {item.rationale}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Ranked grid */}
          <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-4">
            {revealStep >= 2 ? 'Rankings' : 'Results'}
          </div>
          <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 ${revealStep < 1 ? 'blur-sm' : ''} transition-all duration-700`}>
            {(revealStep >= 2 ? ranked : photos.filter(p => p.status === 'done')).map((p, i) => {
              const isWinner = revealStep >= 3 && winner?.photoId === p.photoId;
              return (
                <div
                  key={p.photoId || i}
                  onClick={() => p.photoId && navigate(`/results/${p.photoId}`)}
                  className={`card-hover p-0 overflow-hidden cursor-pointer transition-all duration-500 ${isWinner ? 'ring-2 ring-rose/40' : ''}`}
                >
                  <div className="aspect-square overflow-hidden relative">
                    <img src={p.preview} alt="" className="w-full h-full object-cover" />
                    {isWinner && revealStep >= 3 && (
                      <div className="absolute top-2 left-2 bg-rose text-white text-[10px] font-sans font-medium px-2 py-0.5 rounded">
                        #1
                      </div>
                    )}
                    {!isWinner && revealStep >= 2 && (
                      <div className="absolute top-2 left-2 bg-black/40 text-white text-[10px] font-sans px-2 py-0.5 rounded">
                        #{i + 1}
                      </div>
                    )}
                  </div>
                  <div className="p-3 text-center">
                    {revealStep >= 1 && p.score != null ? (
                      <>
                        <div className={`text-2xl font-sans ${scoreColor(p.score)} mb-0.5`} style={{ fontWeight: 600 }}>{p.score}</div>
                        <div className="text-[10px] text-ink-faint">{scoreLabel(p.score)}</div>
                      </>
                    ) : (
                      <div className="skeleton h-8 w-16 mx-auto rounded" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          {revealStep >= 3 && (
            <div className="flex justify-center gap-3 mt-10">
              <button onClick={() => { setPhase('upload'); setPhotos([]); }} className="btn-primary text-xs">New compare</button>
              <button onClick={() => navigate(`/compare-details?project=${getProjectIdForVertical(vertical)}`)} className="btn-secondary text-xs">Compare details</button>
              <button onClick={() => navigate('/app')} className="btn-ghost text-xs">Back</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
